import { useState } from 'react';
import { Input } from 'reactstrap';
import styled from 'styled-components';
import { AddressAutofill } from '@mapbox/search-js-react';
import { uniqueId } from 'lodash';
import { parseCoords } from '../../../utils/geocode';

const AddressInput = styled(Input)`
  width: 450px;
  max-width: calc(100vw - 32px);
`;

// submit is a function in the form `({ address, center }) => void`
const AddressSearch = ({
  submit,
  className,
  conusOnly = false,
  ...inputProps
}) => {
  const [value, setValue] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  return (
    <AddressAutofill
      theme={{
        cssText: `[class$="--Results"], [class*="--Results "] {
          /* Set a z-index on the results so they appear above the backdrop. */
          z-index: 1060;

          /* Fix issue for very narrow screens */
          max-width: calc(100vw - 32px);
          min-width: min(300px, calc(100vw - 32px));
        }`,
      }}
      options={
        conusOnly
          ? { country: 'US' } // only return US results
          : undefined
      }
      accessToken={process.env.REACT_APP_MAPBOX_API_KEY}
      onRetrieve={({ features }) => {
        const [feature] = features;
        if (!feature) return;
        const { coordinates } = feature.geometry;
        const { place_name: placeName } = feature.properties ?? {};

        submit({
          address: placeName ?? value,
          center: {
            lng: coordinates[0],
            lat: coordinates[1],
          },
        });
      }}
      onSuggest={({ suggestions }) => {
        // NOTE: this is a HACK that fixes a bug in the mapbox-search-js library.
        // The bug is that 'street' suggestions don't work when clicked.
        // The fix is to change 'street' to something else 🙃
        suggestions.forEach((suggestion) => {
          // eslint-disable-next-line no-param-reassign
          if (suggestion.accuracy === 'street') suggestion.accuracy = 'rooftop';
        });
      }}
    >
      <AddressInput
        className={className}
        name={uniqueId('address-')} // Random name to prevent Chrome autofill
        placeholder="(e.g. 123 Main St. or 40.712,-74.004)"
        autoComplete="off" // Disable Chrome autofill
        form={uniqueId('form-')} // Random form to prevent Chrome autofill
        bsSize="md"
        value={isLoading ? 'Loading...' : value}
        maxLength={250}
        onChange={(e) => setValue(e.target.value)}
        autoFocus // focus this element on load
        disabled={isLoading}
        onKeyDown={async (e) => {
          // Only submit on Enter
          if (e.key !== 'Enter') return;

          const input = e.target.value.trim();
          setIsLoading(true);
          try {
            if (input.length > 0) {
              // if the input is a valid lat/lng (or lng/lat), submit it directly without doing a geocode lookup
              const coords = parseCoords(input, { conusOnly });
              if (coords) {
                submit({ center: coords, address: '' });
              }
            }
          } catch (err) {
            console.error(err);
          } finally {
            setIsLoading(false);
          }
        }}
        {...inputProps}
      />
    </AddressAutofill>
  );
};

export default AddressSearch;
